import { Card, Flex, Text } from "@jsq/ds/core";
import { type GetComplianceCaseQuery } from "~/graphql/supergraphTypes";
import { getInvestingProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { useComplianceEntityOnboardingEnabled } from "~/pages/Compliance/flags";
import { isInternalEscalationSource } from "~/pages/Compliance/utils/constants";
import { SPACING } from "~/styles/variables";
import { toDbId } from "~/utils/graphql-helpers";
import { CustomLabeledData } from "../CaseDetailsCard/CustomLabeledData";

interface Props {
  complianceCase: GetComplianceCaseQuery["complianceCase"] | undefined;
}

export const EscalationDetailsCard = ({ complianceCase }: Props) => {
  const internalEscalationsEnabled = useComplianceEntityOnboardingEnabled();
  const showAffectedPositions = !(
    internalEscalationsEnabled &&
    isInternalEscalationSource(complianceCase?.sourceCase?.caseType)
  );
  const originCaseName =
    complianceCase?.sourceCase?.name ||
    getInvestingProfileName(complianceCase?.sourceCase?.investingProfile);
  const originCaseUrl = `/canopy/compliance/case/${complianceCase?.sourceCase?.id ?? ""}`;

  const profileName = getInvestingProfileName(complianceCase?.investingProfile);

  const complianceCases = complianceCase?.investingProfile?.complianceCases || [];
  const affectedPositionsCount = complianceCases.length;
  const escalationIssue = complianceCase?.escalations?.[0]?.description ?? "--";

  // Extract position IDs from compliance cases
  const positionIds = complianceCases
    .map((caseMap) => {
      const positionId = caseMap.complianceCase?.position?.id;
      return typeof positionId === "string" ? positionId : null;
    })
    .filter((id): id is string => id !== null);

  // Create URL with position IDs and arena ID
  const arenaObjectId = complianceCase?.externalArena?.objectId;
  const affectedPositionsUrl =
    positionIds.length > 0 && arenaObjectId != null
      ? `/canopy/compliance/positions?arenaIds=${String(arenaObjectId)}&positionIds=${positionIds.map((id) => toDbId(id)).join(",")}`
      : "";
  return (
    <Card>
      <Flex
        css={{ marginBottom: SPACING.LARGE }}
        alignContent="center"
        alignItems="center"
        justifyContent="space-between"
      >
        <Text variant="secondary-md">DETAILS</Text>
      </Flex>
      <Flex flexDirection="column" gap={8}>
        <CustomLabeledData
          label="Origin case"
          data={originCaseName ?? "--"}
          subdata=""
          url={originCaseUrl}
        />
        <CustomLabeledData label="Profile" data={profileName ?? "--"} subdata="" url="" />
        {showAffectedPositions && (
          <CustomLabeledData
            label="Affected positions"
            data={String(affectedPositionsCount)}
            subdata=""
            url={affectedPositionsUrl}
          />
        )}
        <CustomLabeledData
          label="Escalation issue"
          data={escalationIssue}
          subdata=""
          url=""
          dataMaxWidthPx={544}
        />
      </Flex>
    </Card>
  );
};
